import cn from 'classnames';
import React, { ChangeEvent, FC, ReactNode, memo } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import {
  FormControl,
  FormHelperText,
  InputAdornment,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';

import Button from '@components/common/Button';
import TokensIcon from '@components/common/TokensIcon';

import { Size, TokenSize, Variant } from '@constants/common/ui.constants';

import useStyles from './styles.module';

type Props = TextFieldProps & {
  label?: string | ReactNode;
  inputTitle?: string | ReactNode;
  value?: string | number | null;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  helperText?: string;
  formClassName?: string;
  inputTitleClassName?: string;
  placeholder?: string;
  inputError?: string | null;
  unitEnd?: string | ReactNode;
  unitStart?: string | ReactNode;
  endAdornmentClass?: string;
  onMax?: () => void;
  disabled?: boolean;
  isSearch?: boolean;
  isDark?: boolean;
  rows?: number;
  selectedToken?: {
    icons: { address: string; chainId: number }[];
    symbol: string;
  };
  onOpenSelectToken?: () => void;
  readOnly?: boolean;
  buttonMaxClassName?: string;
};

const Input: FC<Props> = ({
  label = '',
  inputTitle = '',
  value,
  onChange,
  helperText = '',
  formClassName = '',
  inputTitleClassName = '',
  buttonMaxClassName = '',
  disabled = false,
  placeholder = '',
  inputError = null,
  size = Size.Small,
  onMax = undefined,
  unitEnd = undefined,
  endAdornmentClass = '',
  unitStart = undefined,
  isSearch = false,
  isDark = false,
  selectedToken,
  onOpenSelectToken,
  readOnly = false,
  rows = undefined,
  ...props
}) => {
  const { classes } = useStyles({
    hasHelperText: !!helperText,
    disabled,
    isError: !!inputError,
  });

  return (
    <div className={classes.root}>
      {inputTitle && (
        <Typography
          variant="subtitle2"
          color={(theme) => theme.primary.p3}
          marginBottom="8px"
          className={inputTitleClassName}
        >
          {inputTitle}
        </Typography>
      )}
      <FormControl
        className={cn(
          classes.formControl,
          {
            [classes.disabledBox]: disabled,
            [classes.errorBox]: inputError,
            [classes.emptyValue]: !value,
            [classes.darkForm]: isDark,
            [classes.withDropdown]: !!selectedToken,
            [classes.readOnlyBox]: readOnly,
          },
          formClassName,
        )}
      >
        <TextField
          fullWidth
          label={label}
          value={value}
          onChange={onChange}
          disabled={disabled}
          placeholder={placeholder}
          // @ts-ignore
          size={size}
          variant={Variant.Outlined}
          className={classes.input}
          autoComplete="off"
          multiline={!!rows}
          rows={rows}
          {...props}
          InputProps={{
            readOnly,
            endAdornment: unitEnd && (
              <InputAdornment position="end" className={endAdornmentClass}>
                <Typography
                  variant="subtitle2"
                  color={(theme) => theme.primary.p3}
                >
                  {unitEnd}
                </Typography>
              </InputAdornment>
            ),
            startAdornment: (unitStart || isSearch) && (
              <InputAdornment position="start" className={endAdornmentClass}>
                <Typography
                  variant="subtitle2"
                  color={(theme) => theme.primary.p3}
                >
                  {isSearch ? (
                    <SearchIcon className={classes.searchIcon} />
                  ) : (
                    unitStart
                  )}
                </Typography>
              </InputAdornment>
            ),
          }}
        />
        <div className={classes.actionsBox}>
          {onMax && (
            <button
              className={cn(classes.buttonMax, buttonMaxClassName)}
              onClick={onMax}
              disabled={disabled}
              type="button"
            >
              <Typography variant="subtitle2">MAX</Typography>
            </button>
          )}
          {selectedToken && (
            <Button
              size={Size.Small}
              variant={isDark ? Variant.Secondary : Variant.SecondaryDark}
              className={classes.selectedTokenBtn}
              onClick={onOpenSelectToken}
              disabled={disabled}
              startIcon={
                <TokensIcon
                  size={TokenSize.Medium}
                  tokens={selectedToken.icons}
                />
              }
              endIcon={<KeyboardArrowDownIcon className={classes.arrowIcon} />}
            >
              {selectedToken.symbol}
            </Button>
          )}
        </div>
        {helperText && (
          <FormHelperText
            className={cn(classes.supportText, classes.formHelperText)}
          >
            <Typography
              variant="caption1"
              color={(theme) => theme.secondary.s3}
            >
              {helperText}
            </Typography>
          </FormHelperText>
        )}
        {inputError && (
          <div className={cn(classes.supportText, classes.error)}>
            <Typography variant="caption1" color={(theme) => theme.accent.a6}>
              {inputError}
            </Typography>
          </div>
        )}
      </FormControl>
    </div>
  );
};

export default memo(Input);
